import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { retrySupabaseOperation } from '@/lib/error-handling';
import { useAuth } from './useAuth';
import { useUserRole } from './useUserRole';

export const useSessionTracking = () => {
  const { user } = useAuth();
  const { role: currentRole } = useUserRole();
  const [sessionId, setSessionId] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !currentRole) return;

    let mounted = true;
    let heartbeatInterval: NodeJS.Timeout | null = null;

    const initSession = async () => {
      try {
        // Use crypto.randomUUID() for truly unique session IDs
        const newSessionId = crypto.randomUUID();
        
        if (!mounted) return;
        setSessionId(newSessionId);

        // First, try to delete any existing session for this user to avoid conflicts
        // Use retry logic for 503 errors
        const deleteResult = await retrySupabaseOperation(
          () => supabase
            .from('user_sessions')
            .delete()
            .eq('user_id', user.id),
          2, // Max 2 retries for cleanup
          500 // Shorter initial delay for cleanup
        );

        // Silently handle delete errors - non-critical
        if (deleteResult.error) {
          const error = deleteResult.error;
          // Only log non-503 errors, 503s are handled by retry logic
          if (error.code !== '503' && error.status !== 503 && !error.message?.includes('503')) {
            if (error.code === '42P01' || error.code === 'PGRST116' || error.message?.includes('permission denied') || error.message?.includes('does not exist')) {
              logger.debug('Session tracking table may not exist or user lacks permission (non-critical)', { error });
            } else {
              logger.debug('Session tracking delete error (non-critical)', { error });
            }
          }
        }

        // Insert new session (no conflict since we deleted old ones)
        // Use retry logic for 503 errors
        const insertResult = await retrySupabaseOperation(
          () => supabase.from('user_sessions').insert({
            user_id: user.id,
            session_id: newSessionId,
            role: currentRole,
            last_activity_at: new Date().toISOString(),
          }),
          3, // Max 3 retries for insert
          1000 // 1 second initial delay
        );

        if (insertResult.error) {
          const error = insertResult.error;
          // Handle table not existing or permission errors gracefully
          if (error.code === '42P01' || error.code === 'PGRST116' || error.message?.includes('permission denied') || error.message?.includes('does not exist')) {
            logger.debug('Session tracking table may not exist or user lacks permission (non-critical)', { error });
            return;
          }
          // Don't log 503 errors - they're handled by retry logic
          if (error.code !== '503' && error.status !== 503 && !error.message?.includes('503')) {
            logger.debug('Session tracking error (non-critical)', { error });
          }
          return;
        }

        // Heartbeat to update last_activity_at
        heartbeatInterval = setInterval(async () => {
          if (!mounted || !user) return;
          
          // Use retry logic for heartbeat updates
          const updateResult = await retrySupabaseOperation(
            () => supabase
              .from('user_sessions')
              .update({ last_activity_at: new Date().toISOString() })
              .eq('session_id', newSessionId)
              .eq('user_id', user.id),
            1, // Only 1 retry for heartbeat (non-critical)
            500 // Short delay
          );
          
          // Silently handle errors - session tracking is non-critical
          // Don't log 503 errors - they're handled by retry logic
          if (updateResult.error) {
            const error = updateResult.error;
            if (error.code !== '503' && error.status !== 503 && !error.message?.includes('503')) {
              logger.debug('Session heartbeat update failed (non-critical)', { error });
            }
          }
        }, 60000);
      } catch (error) {
        logger.error('Failed to initialize session', { error });
      }
    };

    // Debounce initialization
    const timeoutId = setTimeout(initSession, 100);

    // Cleanup on unmount
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
      // FIXED: Use proper async cleanup with error handling
      // Note: We capture sessionId in closure since state may be stale
      const cleanupSession = async (sid: string | null) => {
        if (!sid) return;
        try {
          // Use retry logic for cleanup
          const deleteResult = await retrySupabaseOperation(
            () => supabase.from('user_sessions').delete().eq('session_id', sid),
            1, // Only 1 retry for cleanup
            500 // Short delay
          );
          
          // Silently handle errors - non-critical
          if (deleteResult.error) {
            const error = deleteResult.error;
            // Don't log 503 errors - they're handled by retry logic
            if (error.code !== '503' && error.status !== 503 && !error.message?.includes('503')) {
              logger.debug('Failed to cleanup session on unmount', { error });
            }
          }
        } catch (error) {
          // Non-critical - session will be cleaned up by TTL
          logger.debug('Failed to cleanup session on unmount', { error });
        }
      };
      cleanupSession(sessionId);
    };
  }, [user, currentRole, sessionId]);

  return { sessionId };
};
